import { useEffect, useState } from 'react';
import {
    RichEditor,
    isDocEmpty,
    collectDocImageFileIds,
    applyResolvedUrlsToDoc
} from '@odo/shared-ui';
import type { RichEditorImageUploadHandler, TiptapDoc } from '@odo/shared-ui';
import { resolvePracticeImageUrls } from '../../../../services/practiceImages';
import type { CommunicationEntry } from '../../../../services/communications';

// Read-only render only - RichEditor never calls these while readOnly.
const NOOP_CHANGE = (): void => {};
const NOOP_UPLOAD: RichEditorImageUploadHandler = () => Promise.reject(new Error('read-only'));

interface EntryBodyProps {
    entry: CommunicationEntry;
    /** Null on a practice-less thread - image references never re-resolve there (nowhere to look them up). */
    practiceId: string | null;
}

/** Renders an entry's body read-only; images re-resolve to fresh signed
 *  URLs first. Plain-text-only entries fall back to plain text. */
export function EntryBody({ entry, practiceId }: EntryBodyProps) {
    const hasDoc = Boolean(entry.bodyDoc) && !isDocEmpty(entry.bodyDoc as TiptapDoc);
    const [doc, setDoc] = useState<TiptapDoc | null>(entry.bodyDoc ?? null);

    useEffect(() => {
        if (!entry.bodyDoc || !practiceId) {
            setDoc(entry.bodyDoc ?? null);
            return;
        }
        setDoc(entry.bodyDoc);
        const fileIds = collectDocImageFileIds(entry.bodyDoc);
        if (fileIds.length === 0) return;

        let cancelled = false;
        resolvePracticeImageUrls(practiceId, fileIds)
            .then((urls) => {
                if (!cancelled && entry.bodyDoc) {
                    setDoc(applyResolvedUrlsToDoc(entry.bodyDoc, urls));
                }
            })
            .catch(() => {
                /* leave unresolved - text stays intact, images may render broken */
            });
        return () => {
            cancelled = true;
        };
    }, [entry.bodyDoc, practiceId]);

    if (!hasDoc || !doc) {
        if (!entry.body) return null;
        return (
            <p className="text-[14px] text-gray-800 leading-[1.65] whitespace-pre-wrap">
                {entry.body}
            </p>
        );
    }

    return (
        <div className="entry-rich-body">
            <RichEditor
                value={doc}
                onChange={NOOP_CHANGE}
                onImageUpload={NOOP_UPLOAD}
                readOnly
                chrome="bare"
            />
        </div>
    );
}
